export const RESTORE_DESTINATION_UNASSIGNED = '__restore_unassigned__';
export const RESTORE_DESTINATION_ARCHIVE = '__restore_archive__';

export type RestoreDestination =
  | typeof RESTORE_DESTINATION_UNASSIGNED
  | typeof RESTORE_DESTINATION_ARCHIVE
  | string;

interface RestoreCardData {
  stepId?: string | null;
}

export interface RestoreCardMutation {
  stepId: string | null;
  shouldSetDoneAt: boolean;
  shouldClearDoneAt: boolean;
  shouldUpdateStepEnteredAt: boolean;
}

interface InsertCardIdsAtBoundaryParams {
  currentCardIds: string[];
  insertedCardIds: string[];
  targetCardIds: string[];
  insertAt: 'before-first' | 'after-last';
  reverseInsertedCardIds?: boolean;
}

export const getRestoreDestinationDisplayLabel = (destination: RestoreDestination): string => {
  if (destination === RESTORE_DESTINATION_UNASSIGNED) return 'Pending';
  if (destination === RESTORE_DESTINATION_ARCHIVE) return 'Done';
  return destination;
};

export const insertCardIdsAtBoundary = ({
  currentCardIds,
  insertedCardIds,
  targetCardIds,
  insertAt,
  reverseInsertedCardIds = false,
}: InsertCardIdsAtBoundaryParams): string[] => {
  const orderedInsertedCardIds = reverseInsertedCardIds
    ? [...insertedCardIds].reverse()
    : insertedCardIds;

  if (targetCardIds.length === 0) {
    return insertAt === 'before-first'
      ? [...orderedInsertedCardIds, ...currentCardIds]
      : [...currentCardIds, ...orderedInsertedCardIds];
  }

  const firstTargetIdx = currentCardIds.indexOf(targetCardIds[0]);
  const lastTargetIdx = currentCardIds.indexOf(targetCardIds[targetCardIds.length - 1]);
  const insertIdx = insertAt === 'before-first' ? Math.max(0, firstTargetIdx) : lastTargetIdx + 1;

  return [
    ...currentCardIds.slice(0, insertIdx),
    ...orderedInsertedCardIds,
    ...currentCardIds.slice(insertIdx),
  ];
};

export const getRestoreCardMutation = (
  destination: RestoreDestination,
  cardData: RestoreCardData,
): RestoreCardMutation => {
  if (destination === RESTORE_DESTINATION_ARCHIVE) {
    return {
      stepId: cardData.stepId ?? null,
      shouldSetDoneAt: true,
      shouldClearDoneAt: false,
      shouldUpdateStepEnteredAt: false,
    };
  }

  if (destination === RESTORE_DESTINATION_UNASSIGNED) {
    return {
      stepId: null,
      shouldSetDoneAt: false,
      shouldClearDoneAt: true,
      shouldUpdateStepEnteredAt: true,
    };
  }

  return {
    stepId: destination,
    shouldSetDoneAt: false,
    shouldClearDoneAt: true,
    shouldUpdateStepEnteredAt: true,
  };
};
